<script setup lang="ts">
import { computed, PropType } from 'vue'
import { CheckboxField, ChoiceField, TextField, type Field } from '@haskoning/vue-component-library'
import type { InputType } from '@/stores/types/types'
import RadioGroup from '@/components/form/RadioGroup.vue'

const props = defineProps({
	field: {
		type: Object as PropType<Field>,
		required: true
	},
	modelValue: {
		type: Object as PropType<Record<string, any>>,
		required: true
	},
	isReadOnly: {
		type: Boolean,
		default: false
	},
	disabled: {
		type: Boolean,
		default: false
	},
	type: {
		type: [String, null] as PropType<InputType | null>,
		default: null
	}
})

const emit = defineEmits<{
	(e: 'update:modelValue', value: Record<string, any>): void
	(e: 'update'): void
}>()

const vModel = computed({
	get: () => props.modelValue,
	set: (value: Record<string, any>) => {
		emit('update:modelValue', value)
	}
})

const inputType = computed<InputType>(() => props.type ?? (props.field.type as InputType) ?? 'text')
const shouldUseRadio = computed(() => inputType.value == 'radio')
const shouldUseCheckbox = computed(() => ['checkbox', 'boolean'].includes(inputType.value))
const shouldUseTextBox = computed(() => ['text', 'string'].includes(inputType.value))
const shouldUseNumberTextBox = computed(() =>
	['number', 'float', 'integer', 'decimal'].includes(inputType.value)
)
const shouldUseChoice = computed(() => ['choice', 'list'].includes(inputType.value))
</script>

<template>
	<ChoiceField
		v-if="shouldUseChoice"
		:field="field"
		v-model="vModel"
		:choices="field.choices"
		@update="emit('update')"
		no-help-text
		v-bind="$attrs"
		:menu-props="{ zIndex: 9999 }"
	/>
	<RadioGroup
		v-else-if="shouldUseRadio"
		:field="field"
		v-model="vModel"
		@update="emit('update')"
		v-bind="$attrs"
		inline
		class="mb-2"
		hide-details
	/>
	<CheckboxField
		v-else-if="shouldUseCheckbox"
		:field="field"
		v-model="vModel"
		:no-help-text="true"
		:readonly="isReadOnly"
		@update="emit('update')"
		hide-details
	/>
	<TextField
		v-else-if="shouldUseTextBox || shouldUseNumberTextBox"
		:field="field"
		v-model="vModel"
		:no-help-text="true"
		:type="shouldUseNumberTextBox ? 'number' : 'text'"
		:readonly="isReadOnly"
		:disabled="disabled"
		@update-on-blur="emit('update')"
	/>
</template>
